import { SortDirectionType } from '../components/ClinTableOrderToggle/ClinTableOrderToggle'
import {
  getProgramFromSearchIndexById,
  programsEnrolled
} from '../services/ApiService'
import {
  PatientDetailsDto,
  ProgramCatalogDto,
  ProgramCountryDto
} from '../types/swaggerTypes'
import {
  appendCastorIdpHint,
  getCastorEdcUrlFromStudyId,
  getValidCastorEdcUrl
} from './rwdFollowUpEligibility'

export interface IRwdNavProgramContext {
  visible: boolean
  castorUrl: string
}

export const isRwdNavContextVisible = (
  context: IRwdNavProgramContext | null | undefined
): boolean => context?.visible === true && !!context.castorUrl?.trim()

export type RwdNavContextSource = 'institute' | 'page'

/**
 * Institute-level visibility is a floor (CLOS-13914). A detail page resolves a
 * verdict about ONE programme or ONE patient, which is narrower than the
 * enrolment-wide scan the nav item is specified against: CLOS-13515 scopes
 * visibility to the user's enrolment at login, not to the page they are on.
 * Page-sourced writes may therefore only RAISE visibility; only institute-
 * sourced writes may lower it.
 */
export const mergeRwdNavProgramContext = (
  previous: IRwdNavProgramContext | null,
  next: IRwdNavProgramContext | null,
  source: RwdNavContextSource
): IRwdNavProgramContext | null => {
  if (source === 'institute') {
    return next
  }

  if (isRwdNavContextVisible(next)) {
    return next
  }

  return isRwdNavContextVisible(previous) ? previous : next
}

/**
 * Patient-detail RWD nav must not overwrite context while required data is still
 * loading. Applying `null` during that window causes visible→hidden→visible flicker
 * (CLOS-13947) when navigating from an already-visible programme context.
 */
export const shouldDeferPatientRwdNavUpdate = ({
  patient,
  program,
  isPatientLoading,
  isProgramLoading
}: {
  patient?: PatientDetailsDto
  program?: ProgramCatalogDto
  isPatientLoading: boolean
  isProgramLoading: boolean
}): boolean => {
  if (isPatientLoading || !patient) {
    return true
  }

  // Programme catalog gates Castor study URL / country RWD flags. Wait until it
  // is present when the patient belongs to a programme — do not treat a missing
  // program as a definitive "hide" while the request is in flight or between
  // patient-load and useProgram effect start.
  if (patient.program?.programId != null) {
    if (isProgramLoading || !program) {
      return true
    }
  }

  return false
}

const MAX_RWD_PROGRAM_LOOKUPS = 20
const RWD_PROGRAM_LOOKUP_CONCURRENCY = 6

interface IRwdProgramScan {
  rwdEnabledProgramIds: Set<number>
  firstContext: IRwdNavProgramContext | null
  /** False when any request in the pass failed, so the verdict is not trustworthy. */
  complete: boolean
}

const contextCache = new Map<string, IRwdNavProgramContext | null>()
const rwdProgramScanCache = new Map<string, IRwdProgramScan>()

const isRwdEnrolled = (flag?: 'Y' | 'N' | null): boolean => flag === 'Y'

export const isProgramCountryRwdEnabled = (
  programCountry?: ProgramCountryDto | null
): boolean => {
  if (!programCountry) {
    return false
  }

  const rwdFlag = programCountry.realWorldDataFlag
  const hasRwdFlag = rwdFlag === 'Y' || rwdFlag === 'Yes'
  const hasRwdImport = programCountry.rwdImport === 'Yes'

  return hasRwdFlag && hasRwdImport
}

export const isProgramCountryRwdNavEnabled = (
  programCountry?: ProgramCountryDto | null
): boolean =>
  isProgramCountryRwdEnabled(programCountry) &&
  !!programCountry?.castorStudyId?.trim()

export const resolveRwdNavProgramContext = (
  program: ProgramCatalogDto | undefined,
  portfolioCountryCode?: string
): IRwdNavProgramContext | null => {
  if (!program) {
    return null
  }

  const programCountry = resolveProgramCountryForPortfolio(
    program.programCountry,
    program.programCountries,
    portfolioCountryCode
  )

  if (!isProgramCountryRwdNavEnabled(programCountry)) {
    return null
  }

  return {
    visible: true,
    castorUrl: getCastorEdcUrlFromStudyId(programCountry!.castorStudyId)
  }
}

/**
 * Programme-detail RWD nav must not publish visibility from programme config
 * alone. Enrolment resolves separately from the catalog, so the write is held
 * until both have settled — writing early would either hide an already-visible
 * nav or show one for a programme the user is not enrolled on (CLOS-14149).
 */
export const shouldDeferProgramRwdNavUpdate = ({
  program,
  isProgramLoading,
  isEnrolledProgramsLoading,
  isEnrolmentResolved
}: {
  program?: ProgramCatalogDto
  isProgramLoading: boolean
  isEnrolledProgramsLoading: boolean
  isEnrolmentResolved: boolean
}): boolean =>
  isProgramLoading ||
  !program ||
  isEnrolledProgramsLoading ||
  !isEnrolmentResolved

/**
 * Programme-detail page RWD nav context. Programme-country RWD config makes a
 * programme *capable* of RWD; only enrolment makes the nav applicable to this
 * user (CLOS-14149).
 */
export const resolveEnrolledProgramRwdNavContext = (
  program: ProgramCatalogDto | undefined,
  portfolioCountryCode: string | undefined,
  isEnrolledOnProgram: boolean
): IRwdNavProgramContext | null => {
  if (!isEnrolledOnProgram) {
    return null
  }

  return resolveRwdNavProgramContext(program, portfolioCountryCode)
}

export const resolveProgramCountryForPortfolio = (
  programCountry?: ProgramCountryDto,
  programCountries?: ProgramCountryDto[],
  portfolioCountryCode?: string
): ProgramCountryDto | undefined => {
  if (!portfolioCountryCode) {
    return programCountry
  }

  if (programCountry?.countryId === portfolioCountryCode) {
    return programCountry
  }

  return programCountries?.find(
    (country) => country.countryId === portfolioCountryCode
  )
}

const hasRwdNominationDetails = (detail: PatientDetailsDto): boolean => {
  const nomination = detail.rwdNominationDetails
  if (!nomination) {
    return false
  }

  return !!(
    nomination.fullName?.trim() ||
    nomination.emailAddress?.trim() ||
    nomination.classification?.trim()
  )
}

const isPatientRwdOptedIn = (
  detail: PatientDetailsDto | undefined,
  programId: number | undefined,
  rwdEnabledProgramIds: Set<number>
): boolean => {
  if (!detail) {
    return false
  }

  const resolvedProgramId = programId ?? detail.program?.programId

  if (isRwdEnrolled(detail.realWorldDataEnrolFlag)) {
    if (rwdEnabledProgramIds.size === 0 || resolvedProgramId == null) {
      return true
    }

    return rwdEnabledProgramIds.has(resolvedProgramId)
  }

  if (
    resolvedProgramId != null &&
    rwdEnabledProgramIds.has(resolvedProgramId) &&
    hasRwdNominationDetails(detail)
  ) {
    return true
  }

  return false
}

export const resolveRwdNavContextFromPatientDetail = (
  patient: PatientDetailsDto | undefined,
  program: ProgramCatalogDto | undefined,
  portfolioCountryCode?: string
): IRwdNavProgramContext | null => {
  if (!patient) {
    return null
  }

  const programContext = resolveRwdNavProgramContext(
    program,
    portfolioCountryCode
  )

  if (!programContext) {
    return null
  }

  const programId = patient.program?.programId ?? program?.projectId
  const rwdEnabledProgramIds =
    programId != null ? new Set<number>([programId]) : new Set<number>()

  if (!isPatientRwdOptedIn(patient, programId, rwdEnabledProgramIds)) {
    return null
  }

  const patientCastorUrl = getValidCastorEdcUrl(patient.castorEdcUrl)

  return {
    visible: true,
    castorUrl: patientCastorUrl
      ? appendCastorIdpHint(patientCastorUrl)
      : programContext.castorUrl
  }
}

/**
 * Patient-detail page RWD nav context.
 * Prefer a patient Castor deep-link when the patient is RWD opted-in; otherwise
 * fall back to programme-level visibility (CLOS-13515 / CLOS-13762). This avoids
 * clearing a previously visible programme nav when opening a non-opted-in patient
 * on an RWD-enabled programme (CLOS-13947 flicker: visible→hidden→visible).
 */
export const resolveRwdNavContextForPatientDetailPage = (
  patient: PatientDetailsDto | undefined,
  program: ProgramCatalogDto | undefined,
  portfolioCountryCode?: string
): IRwdNavProgramContext | null => {
  const patientContext = resolveRwdNavContextFromPatientDetail(
    patient,
    program,
    portfolioCountryCode
  )

  if (patientContext) {
    return patientContext
  }

  return resolveRwdNavProgramContext(program, portfolioCountryCode)
}

const RWD_NAV_SESSION_PREFIX = 'rwdNav'

/**
 * Key for a remembered verdict. Scoped to the signed-in subject as well as the
 * institute, because enrolment is per user: two people sharing a workstation
 * must not inherit each other's nav (CLOS-14240).
 */
export const buildRwdNavSessionKey = (
  userSub?: string,
  instituteId?: number | string | null,
  portfolioCountryCode?: string
): string | null =>
  userSub && instituteId != null
    ? `${RWD_NAV_SESSION_PREFIX}:${userSub}:${instituteId}:${
        portfolioCountryCode ?? 'unknown'
      }`
    : null

/**
 * Returns undefined when nothing is remembered, which is distinct from a
 * remembered null meaning "resolved, and the item does not belong here".
 * sessionStorage rather than localStorage: it is per tab, whereas localStorage
 * is shared browser-wide and has already caused a cross-account defect here.
 */
export const readRwdNavSessionContext = (
  key: string | null
): IRwdNavProgramContext | null | undefined => {
  if (!key) {
    return undefined
  }

  try {
    const raw = sessionStorage.getItem(key)
    return raw == null
      ? undefined
      : (JSON.parse(raw) as IRwdNavProgramContext | null)
  } catch {
    return undefined
  }
}

export const writeRwdNavSessionContext = (
  key: string | null,
  context: IRwdNavProgramContext | null
): void => {
  if (!key) {
    return
  }

  try {
    sessionStorage.setItem(key, JSON.stringify(context))
  } catch {
    // Storage unavailable or full. The scan still runs, so this is not fatal.
  }
}

export const invalidateRwdNavVisibilityCache = (): void => {
  contextCache.clear()
  rwdProgramScanCache.clear()
}

/**
 * One bounded-parallel pass over the user's enrolled programmes (CLOS-14240).
 * Previously this awaited each programme in turn, so an institute with N
 * enrolled programmes cost N sequential round trips and the nav item stayed
 * hidden for the whole window. The winning programme's context is captured
 * during the pass so the caller does not refetch it.
 */
const scanEnrolledRwdPrograms = async (
  portfolioCountryCode?: string
): Promise<IRwdProgramScan> => {
  const cacheKey = portfolioCountryCode ?? 'unknown'
  const cached = rwdProgramScanCache.get(cacheKey)
  if (cached) {
    return cached
  }

  const scan: IRwdProgramScan = {
    rwdEnabledProgramIds: new Set<number>(),
    firstContext: null,
    complete: false
  }

  let enrolledProgramIds: number[]

  try {
    const response = await programsEnrolled({
      query: '',
      filter: {},
      pagination: {
        skip: 0,
        take: 100
      },
      sorting: {
        sortBy: 'programName',
        order: SortDirectionType.Ascending.toUpperCase()
      }
    })

    enrolledProgramIds = (
      response?.data?.result?.map((program) => program.programId) ?? []
    ).slice(0, MAX_RWD_PROGRAM_LOOKUPS)
  } catch {
    return scan
  }

  // Indexed by enrolment position, so the programme chosen below is stable
  // regardless of which worker finishes first.
  const contexts: (IRwdNavProgramContext | null)[] = enrolledProgramIds.map(
    () => null
  )
  let hadLookupFailure = false
  let cursor = 0

  const scanNextProgram = async (): Promise<void> => {
    while (cursor < enrolledProgramIds.length) {
      const index = cursor
      cursor += 1
      const programId = enrolledProgramIds[index]

      try {
        const programResponse = await getProgramFromSearchIndexById(
          programId.toString()
        )
        const context = resolveRwdNavProgramContext(
          programResponse.data,
          portfolioCountryCode
        )

        if (context) {
          scan.rwdEnabledProgramIds.add(programId)
          contexts[index] = context
        }
      } catch {
        hadLookupFailure = true
      }
    }
  }

  await Promise.all(
    Array.from(
      {
        length: Math.min(
          RWD_PROGRAM_LOOKUP_CONCURRENCY,
          enrolledProgramIds.length
        )
      },
      () => scanNextProgram()
    )
  )

  scan.firstContext = contexts.find((context) => context !== null) ?? null
  scan.complete = !hadLookupFailure

  // A partial scan must not be cached, or one failed lookup pins 'no RWD' until
  // the next institute event bumps the refresh key (CLOS-13914).
  if (scan.complete) {
    rwdProgramScanCache.set(cacheKey, scan)
  }

  return scan
}

const resolveEnrolledRwdNavContext = async (
  portfolioCountryCode?: string
): Promise<IRwdProgramScan> => scanEnrolledRwdPrograms(portfolioCountryCode)

export const resolveRwdNavContext = async (
  cacheKey?: string,
  portfolioCountryCode?: string
): Promise<IRwdNavProgramContext | null> => {
  const resolvedCacheKey = cacheKey
    ? `${cacheKey}:${portfolioCountryCode ?? 'unknown'}`
    : undefined

  if (resolvedCacheKey && contextCache.has(resolvedCacheKey)) {
    return contextCache.get(resolvedCacheKey)!
  }

  try {
    const scan = await resolveEnrolledRwdNavContext(portfolioCountryCode)

    // Only a complete pass is a resolved answer. Memoising a partial one pins
    // 'no RWD' for this institute until the refresh key changes (CLOS-13914).
    if (resolvedCacheKey && scan.complete) {
      contextCache.set(resolvedCacheKey, scan.firstContext)
    }

    return scan.firstContext
  } catch {
    // Deliberately not cached: a thrown scan is a transient failure, not a
    // resolved 'no RWD' answer (CLOS-13914).
    return null
  }
}

export const resolveRwdNavVisible = async (
  cacheKey?: string,
  portfolioCountryCode?: string
): Promise<boolean> => {
  const context = await resolveRwdNavContext(cacheKey, portfolioCountryCode)
  return isRwdNavContextVisible(context)
}
